import {
  editorCharacterStylingInputFromRecord,
  editorTextBoxDocumentFromRecord,
  editorTextBoxDocumentsFromBundle,
} from '@messenger/lotus'
import type {
  EditorCaseData,
  EditorTextBoxDocument,
  EditorTextBoxDocumentOptions,
} from '@messenger/lotus'
import type {
  EditorCharacterStylingInput,
  EditorColorRecord,
  EditorContentRoleValue,
  EditorFontFamilyRecord,
  EditorPageContentRecord,
  EditorTextBoxRecord,
  EditorTextStylingRecord,
} from '@messenger/lotus/row-store'

type WireDates<T, K extends keyof T> = Omit<T, K> & { [P in K]: string }

export type EditorTextBoxWireRecord = WireDates<
  EditorTextBoxRecord,
  'createdAt' | 'updatedAt'
>

export type EditorPageContentBundle = Omit<
  EditorPageContentRecord,
  'boxes' | 'stylings' | 'colors' | 'fontFamilies'
> & {
  boxes: EditorTextBoxWireRecord[]
  stylings: Record<
    string,
    WireDates<EditorTextStylingRecord, 'createdAt' | 'updatedAt'>
  >
  colors: Record<string, WireDates<EditorColorRecord, 'createdAt'>>
  fontFamilies: Record<string, WireDates<EditorFontFamilyRecord, 'createdAt'>>
}

const mapValues = <T, U>(
  record: Record<string, T>,
  map: (value: T) => U,
): Record<string, U> =>
  Object.fromEntries(
    Object.entries(record).map(([key, value]) => [key, map(value)]),
  )

export const editorPageContentFromWire = (
  bundle: EditorPageContentBundle,
): EditorPageContentRecord => ({
  ...bundle,
  boxes: bundle.boxes.map((box) => ({
    ...box,
    createdAt: new Date(box.createdAt),
    updatedAt: new Date(box.updatedAt),
  })),
  stylings: mapValues(bundle.stylings, (styling) => ({
    ...styling,
    createdAt: new Date(styling.createdAt),
    updatedAt: new Date(styling.updatedAt),
  })),
  colors: mapValues(bundle.colors, (color) => ({
    ...color,
    createdAt: new Date(color.createdAt),
  })),
  fontFamilies: mapValues(bundle.fontFamilies, (family) => ({
    ...family,
    createdAt: new Date(family.createdAt),
  })),
})

export type EditorRoleDecorationInputs = Partial<
  Record<EditorContentRoleValue, EditorCharacterStylingInput>
>

// The authoring modal seeds its draft from the current decoration; naming the
// font by verbatim family rather than by id keeps the seed readable without
// the box's refs carrying that id at that instant. The name resolves back to
// the same catalog id on save.
const decorationInputWithFamilyName = (
  input: EditorCharacterStylingInput,
  fontFamilies: EditorPageContentBundle['fontFamilies'],
): EditorCharacterStylingInput => {
  if (input.fontFamilyId === undefined) return input
  const family = fontFamilies[input.fontFamilyId]
  if (family === undefined) return input
  const { fontFamilyId: _dropped, ...rest } = input
  return { ...rest, fontFamily: family.familyName }
}

export const editorRoleDecorationInputs = (
  bundle: EditorPageContentBundle,
): EditorRoleDecorationInputs => {
  const out: EditorRoleDecorationInputs = {}
  for (const decoration of bundle.decorations) {
    const stylingId = decoration.characterStylingId
    if (stylingId === null) continue
    const styling = bundle.stylings[stylingId]
    if (!styling) continue
    out[decoration.role] = decorationInputWithFamilyName(
      editorCharacterStylingInputFromRecord({
        ...styling,
        createdAt: new Date(styling.createdAt),
        updatedAt: new Date(styling.updatedAt),
      }),
      bundle.fontFamilies,
    )
  }
  return out
}

export type EditorTextBoxResolveInputs = {
  caseData: EditorCaseData
  locale: string
}

export const editorTextBoxViewModels = (
  bundle: EditorPageContentBundle,
  inputs: EditorTextBoxResolveInputs,
): Map<string, EditorTextBoxDocument> => {
  const options: EditorTextBoxDocumentOptions = {
    caseData: inputs.caseData,
    locale: inputs.locale,
  }
  const documents = editorTextBoxDocumentsFromBundle(
    editorPageContentFromWire(bundle),
    options,
  )
  return new Map(
    documents.map((document) => [document.resolved.boxId, document]),
  )
}

export type EditorTextBoxViewModelResult =
  | { boxId: string; document: EditorTextBoxDocument; error: null }
  | { boxId: string; document: null; error: string }

const editorTextBoxViewModelResult = (
  box: EditorTextBoxRecord,
  record: EditorPageContentRecord,
  options: EditorTextBoxDocumentOptions,
): EditorTextBoxViewModelResult => {
  try {
    return {
      boxId: box.boxId,
      document: editorTextBoxDocumentFromRecord(box, record, options),
      error: null,
    }
  } catch (error) {
    return {
      boxId: box.boxId,
      document: null,
      error: error instanceof Error ? error.message : String(error),
    }
  }
}

type ViewModelCacheEntry = {
  boxSignature: string
  result: EditorTextBoxViewModelResult
}

type ViewModelCache = {
  sharedSignature: string
  byBox: Map<string, ViewModelCacheEntry>
}

const viewModelCaches = new Map<string, ViewModelCache>()

const sharedResolveSignature = (
  bundle: EditorPageContentBundle,
  inputs: EditorTextBoxResolveInputs,
): string => {
  const { boxes: _boxes, ...shared } = bundle
  return JSON.stringify({ shared, inputs })
}

export const editorTextBoxViewModelResults = (
  bundle: EditorPageContentBundle,
  inputs: EditorTextBoxResolveInputs,
): Map<string, EditorTextBoxViewModelResult> => {
  const cacheKey = JSON.stringify(bundle.anchor)
  const sharedSignature = sharedResolveSignature(bundle, inputs)
  const previous = viewModelCaches.get(cacheKey)
  const reusable =
    previous?.sharedSignature === sharedSignature ? previous.byBox : null
  const record = editorPageContentFromWire(bundle)
  const options: EditorTextBoxDocumentOptions = {
    caseData: inputs.caseData,
    locale: inputs.locale,
  }
  const byBox = new Map<string, ViewModelCacheEntry>()
  const results = new Map<string, EditorTextBoxViewModelResult>()
  bundle.boxes.forEach((wireBox, index) => {
    const boxSignature = JSON.stringify(wireBox)
    const cached = reusable?.get(wireBox.boxId)
    const result =
      cached !== undefined && cached.boxSignature === boxSignature
        ? cached.result
        : editorTextBoxViewModelResult(record.boxes[index], record, options)
    byBox.set(wireBox.boxId, { boxSignature, result })
    results.set(wireBox.boxId, result)
  })
  viewModelCaches.set(cacheKey, { sharedSignature, byBox })
  return results
}
